import { useQuery } from "@tanstack/react-query";
import { matchPath, Navigate, Outlet, useLocation } from "react-router";

import { LoggedInGuard } from "@/authentication/LoggedInGuard";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSessionUserInitial } from "@/hooks/Network/useSessionUserInitial";
import { ConfigUserProvider } from "@/providers/config/ConfigProvider";
import { ProjectAppBrandingConfigProvider } from "@/providers/ProjectAppBrandingConfigProvider";
import { ProjectLoader } from "@/providers/ProjectLoader";
import { PublicAppBrandingProvider } from "@/providers/PublicAppBrandingProvider";
import { QuickReplyProvider } from "@/providers/QuickReplyProvider";
import { StartupLoading } from "@/providers/StartupLoading";
import { useConfigurationQueries } from "@/queries/configurations/queryOptions";
import { routes } from "@/routes";
import { TranslationsProvider } from "@/translations/TranslationsProvider";

import { LocalizedAppProviders, LoggedInProviders } from "./AppProviders";

export function QuickReplyRouteShell(): React.ReactNode {
  return (
    <PublicAppBrandingProvider>
      <QuickReplyProvider>
        <QuickReplySessionContent />
      </QuickReplyProvider>
    </PublicAppBrandingProvider>
  );
}

function QuickReplySessionContent(): React.ReactNode {
  const user = useSessionUser();

  return (
    <TranslationsProvider preferredLanguage={user.language.id}>
      <LocalizedAppProviders>
        <Outlet />
      </LocalizedAppProviders>
    </TranslationsProvider>
  );
}

export function AuthenticationRouteShell(): React.ReactNode {
  return (
    <TranslationsProvider>
      <PublicAppBrandingProvider>
        <LocalizedAppProviders>
          <Outlet />
        </LocalizedAppProviders>
      </PublicAppBrandingProvider>
    </TranslationsProvider>
  );
}

export function RegistrationRouteShell(): React.ReactNode {
  return (
    <LoggedInGuard preserveQueryParams>
      <ConfigUserProvider>
        <PublicAppBrandingProvider>
          <LocalizedAppProviders>
            <Outlet />
          </LocalizedAppProviders>
        </PublicAppBrandingProvider>
      </ConfigUserProvider>
    </LoggedInGuard>
  );
}

export function AuthenticatedRouteShell(): React.ReactNode {
  return (
    <LoggedInGuard>
      <ConfigUserProvider>
        <ProjectLoader>
          <DashboardProjectContent />
        </ProjectLoader>
      </ConfigUserProvider>
    </LoggedInGuard>
  );
}

function DashboardProjectContent(): React.ReactNode {
  const projectId = useProjectId();
  const { data: user, error } = useSessionUserInitial(projectId);
  const configurationQueries = useConfigurationQueries();
  // These requests share the same resolved API/project scope and do not depend on
  // each other's response. The branding provider below observes this same cache entry.
  useQuery(configurationQueries.listAppBrandings());
  if (!user) {
    return error ? <ErrorPage error={error} /> : <StartupLoading />;
  }

  return <DashboardSessionContent />;
}

function DashboardSessionContent(): React.ReactNode {
  const user = useSessionUser();
  const { pathname } = useLocation();
  if (!user.onboardedAt && !matchPath(routes.onboardingFlow.ROOT, pathname)) {
    return <Navigate to={routes.onboardingFlow.slides({ slug: user.project.slug })} replace />;
  }

  const joinProjectConnectionPath = routes.onboardingFlow.joinProjectConnection({ slug: user.project.slug });
  if (user.onboardedAt && user.newConnectionStatus !== "none" && !matchPath(joinProjectConnectionPath, pathname)) {
    return <Navigate to={joinProjectConnectionPath} replace />;
  }

  return (
    <TranslationsProvider preferredLanguage={user.language.id}>
      <ProjectAppBrandingConfigProvider>
        <LoggedInProviders>
          <Outlet />
        </LoggedInProviders>
      </ProjectAppBrandingConfigProvider>
    </TranslationsProvider>
  );
}
